import { useRouter } from 'next/router';
import { useToast } from '@kcd/design-system';

import { PointOnlyBadge } from '~/components/Badge';
import { ShoebillImage } from '~/components/Image';
import { CHECKOUT_ROUTER, VENDORS_ROUTER } from '~/constants/router';
import { useUpdateECoponQuick } from '~/domains/cart/queries';
import ShoppingCartButton from '~/domains/cart/ShoppingCartButton';
import ECouponProductPurchaseButton from '~/domains/products/components/ECouponProductPurchaseButton';
import ProductPrice from '~/domains/products/components/ProductPrice';
import { getOAIErrorMessage } from '~/libs/openapi';
import { OAIApiError } from '~/libs/openApiError';
import type { OAISchema } from '~/types/openapi';
import { openPageModal } from '~/utils/mobileApp';

type Props = {
  product: OAISchema['MarketSelectProductResponseV2'];
  onClick?: () => void;
};

export function HomeCampaignProductItem({ product, onClick }: Props) {
  const router = useRouter();
  const toast = useToast();
  const updateQuickECoupon = useUpdateECoponQuick();

  const onClickPurchase = async () => {
    try {
      await updateQuickECoupon({
        requestBody: {
          offerId: product.offerId!,
        },
      });
      void router.push(CHECKOUT_ROUTER.eCoupon);
    } catch (error) {
      if (error instanceof OAIApiError) {
        const errMessage = getOAIErrorMessage(error);
        toast(errMessage, {
          offset: 86,
        });
      }
      throw error;
    }
  };

  if (!product) return null;

  return (
    <div
      className="flex gap-8"
      role="presentation"
      onClick={() => {
        openPageModal(
          VENDORS_ROUTER.productDetail(product.vendorId, product.offerId!),
        );
        onClick?.();
      }}
    >
      <div className="mb-8 aspect-[1/1] shrink-0 grow-0 basis-[90px] overflow-hidden">
        <div className="relative size-[90px] overflow-hidden rounded-16 border border-solid border-screen-02">
          <ShoebillImage
            width={90}
            height={90}
            className="object-contain"
            src={product.mainImages![0]}
          />
          {product.productType === 'E_COUPON' ? (
            <ECouponProductPurchaseButton
              className="absolute bottom-0 right-0"
              product={product}
              onClickHandler={onClickPurchase}
              ctaText={product.eCouponOrderDisabledLabel}
              disabled={product.eCouponOrderDisabled}
            />
          ) : (
            <ShoppingCartButton
              item={product}
              className="absolute bottom-8 right-8 z-[4]"
            />
          )}
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-4 overflow-hidden">
        {product?.eCouponInfo?.eCouponProductType === 'FREE_POINT_ONLY' && (
          <PointOnlyBadge />
        )}
        <p className="truncate">{product.productName}</p>
        {product?.promotionText && (
          <div className="truncate text-error typo-footnote300-regular">
            {product?.promotionText}
          </div>
        )}
        <ProductPrice {...product} wFull reverse className="!mt-0" />
      </div>
    </div>
  );
}
